import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconScale, IconInfo } from '../../../icons';

interface CommercialRegisterData {
  commercialRegisterNumber: string | null;
  commercialRegisterLocation: string | null;
}

interface CommercialRegisterStepProps {
  initialData: CommercialRegisterData;
  companyName: string;
  onSave: (data: CommercialRegisterData) => Promise<void>;
  onNext: () => void;
  onBack: () => void;
  onSkip: () => void;
}

const COMMON_COURTS = [
  'Berlin (Charlottenburg)',
  'München',
  'Hamburg',
  'Frankfurt am Main',
  'Köln',
  'Düsseldorf',
  'Stuttgart',
  'Mannheim',
  'Nürnberg',
  'Hannover',
  'Dresden',
  'Leipzig',
];

const CommercialRegisterStep: React.FC<CommercialRegisterStepProps> = ({
  initialData,
  companyName,
  onSave,
  onNext,
  onBack,
  onSkip,
}) => {
  const { t } = useTranslation('onboarding');
  const [formData, setFormData] = useState<CommercialRegisterData>({
    commercialRegisterNumber: initialData.commercialRegisterNumber || '',
    commercialRegisterLocation: initialData.commercialRegisterLocation || '',
  });
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<keyof CommercialRegisterData, string>>>({});

  // Keine Validierung mehr - Felder sind optional, auch wenn wir eine Kapitalgesellschaft erkannt haben
  // Der Hinweis informiert den User, dass die Angabe bei Kapitalgesellschaften gesetzlich vorgeschrieben ist

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (errors[name as keyof CommercialRegisterData]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Keine Validierung - User kann auch ohne Angaben fortfahren
    setIsSaving(true);

    try {
      await onSave({
        commercialRegisterNumber: formData.commercialRegisterNumber?.trim() || null,
        commercialRegisterLocation: formData.commercialRegisterLocation?.trim() || null,
      });
      onNext();
    } catch (err) {
      setErrors({ commercialRegisterNumber: t('steps.commercialRegister.errorSaving') });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="max-w-xl mx-auto">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="w-16 h-16 mx-auto bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl flex items-center justify-center mb-4 shadow-lg">
          <IconScale size={32} className="text-primary" />
        </div>
        <h2 className="text-2xl font-bold text-primary mb-2">
          {t('steps.commercialRegister.title')}
        </h2>
        <p className="text-muted">
          {t('steps.commercialRegister.description')}
        </p>
      </div>

      {/* Company Name Display */}
      <div className="bg-[rgb(var(--surface-raised-rgb)/0.5)] rounded-xl p-4 mb-6 border border-hairline">
        <p className="text-muted text-sm">{t('steps.commercialRegister.forCompany')}</p>
        <p className="text-primary font-semibold">{companyName}</p>
      </div>

      {/* Legal Requirement Info */}
      <div className="bg-[var(--tone-warning-bg)] border border-[var(--tone-warning-border)] rounded-xl p-4 mb-6">
        <div className="flex items-start">
          <IconInfo size={20} className="text-[var(--tone-warning-fg)] mt-0.5 mr-3 flex-shrink-0" />
          <div>
            <p className="text-[var(--tone-warning-fg)] text-sm font-medium">
              {t('steps.commercialRegister.legalRequirement')}
            </p>
            <p className="text-[var(--tone-warning-fg)] text-sm mt-1">
              {t('steps.commercialRegister.legalInfo')}
            </p>
          </div>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Register Number */}
        <div>
          <label htmlFor="commercialRegisterNumber" className="block text-sm font-medium text-secondary mb-2">
            {t('steps.commercialRegister.numberLabel')} <span className="text-muted">({t('labels.recommended')})</span>
          </label>
          <input
            type="text"
            id="commercialRegisterNumber"
            name="commercialRegisterNumber"
            value={formData.commercialRegisterNumber || ''}
            onChange={handleChange}
            placeholder={t('steps.commercialRegister.numberPlaceholder')}
            className={`w-full px-4 py-3 bg-surface-raised border ${errors.commercialRegisterNumber ? 'border-red-500' : 'border-strong'} rounded-xl text-primary placeholder:text-faint focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all`}
            autoFocus
          />
          {errors.commercialRegisterNumber && (
            <p className="mt-1 text-[var(--tone-danger-fg)] text-sm">{errors.commercialRegisterNumber}</p>
          )}
          <p className="mt-1 text-faint text-xs">
            {t('steps.commercialRegister.numberHelp')}
          </p>
        </div>

        {/* Register Location */}
        <div>
          <label htmlFor="commercialRegisterLocation" className="block text-sm font-medium text-secondary mb-2">
            {t('steps.commercialRegister.locationLabel')} <span className="text-muted">({t('labels.recommended')})</span>
          </label>
          <input
            type="text"
            id="commercialRegisterLocation"
            name="commercialRegisterLocation"
            value={formData.commercialRegisterLocation || ''}
            onChange={handleChange}
            placeholder={t('steps.commercialRegister.locationPlaceholder')}
            list="courts"
            className={`w-full px-4 py-3 bg-surface-raised border ${errors.commercialRegisterLocation ? 'border-red-500' : 'border-strong'} rounded-xl text-primary placeholder:text-faint focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all`}
          />
          <datalist id="courts">
            {COMMON_COURTS.map(court => (
              <option key={court} value={court} />
            ))}
          </datalist>
          {errors.commercialRegisterLocation && (
            <p className="mt-1 text-[var(--tone-danger-fg)] text-sm">{errors.commercialRegisterLocation}</p>
          )}
          <p className="mt-1 text-faint text-xs">
            {t('steps.commercialRegister.locationHelp')}
          </p>
        </div>

        {/* Where to find info */}
        <div className="bg-[rgb(var(--surface-raised-rgb)/0.5)] rounded-xl p-4 border border-hairline">
          <p className="text-secondary text-sm font-medium mb-2">
            {t('steps.commercialRegister.whereToFind')}
          </p>
          <ul className="space-y-1 text-muted text-sm">
            <li>• {t('steps.commercialRegister.findSource1')}</li>
            <li>• {t('steps.commercialRegister.findSource2')}</li>
            <li>• {t('steps.commercialRegister.findSource3')}</li>
          </ul>
        </div>

        {/* Buttons */}
        <div className="flex justify-between pt-4">
          <button
            type="button"
            onClick={onBack}
            className="px-6 py-3 text-muted hover:text-primary transition-colors"
          >
            {t('common.back')}
          </button>
          
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onSkip}
              className="px-6 py-3 text-muted hover:text-primary border border-strong rounded-xl hover:border-gray-500 transition-all"
            >
              {t('common.skip')}
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="px-8 py-3 bg-indigo-600 text-[#0C1322] font-medium rounded-xl hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg hover:shadow-indigo-500/30"
            >
              {isSaving ? t('common.saving') : t('common.next')}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default CommercialRegisterStep;


